import {CSSProperties, useEffect, useLayoutEffect, useRef, useState} from 'react'
import {DocumentPart} from '@dv5/domain'
import {getActiveOutlinePart, getPartScrollTop} from './documentOutlineHelpers'

const GAP = 8
const NARROW_PANEL_WIDTH = 640

const getViewport = (panel: HTMLElement) => {
    const rect = panel.getBoundingClientRect()
    // The layout can be transformed; part rects also include the document's CSS zoom.
    const scale = panel.offsetHeight ? rect.height / panel.offsetHeight : 1
    const toolbar = panel.querySelector<HTMLElement>(':scope > .layoutMainToolbar')
    const toolbarHeight = (toolbar?.getBoundingClientRect().height || 0) / scale
    return {
        scale,
        toolbarHeight,
        top: rect.top + (panel.clientTop + toolbarHeight + GAP) * scale,
    }
}

export const useDocumentOutline = (
    documentId: string, parts: DocumentPart[], showOutline: boolean, zoom: number, onClose: () => void,
) => {
    const rootRef = useRef<HTMLDivElement>(null)
    const partRefs = useRef(new Map<string, HTMLDivElement>())
    const [activePartId, setActivePartId] = useState<string | null>(null)
    const [highlightedPartId, setHighlightedPartId] = useState<string | null>(null)
    const [geometry, setGeometry] = useState({top: 0, height: 0, width: 0, narrow: false})
    const navigationFrame = useRef<number | null>(null)
    const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    useLayoutEffect(() => {
        const root = rootRef.current
        const panel = root?.closest<HTMLElement>('.dvLayout.noChildren')
        if (!root || !panel) return
        let frame: number | null = null

        const measure = () => {
            frame = null
            const {top, toolbarHeight} = getViewport(panel)
            const next = {
                top: toolbarHeight + GAP,
                height: Math.max(0, panel.clientHeight - toolbarHeight - GAP * 2),
                width: Math.max(0, Math.min(280, panel.clientWidth - GAP * 2)),
                narrow: panel.clientWidth < NARROW_PANEL_WIDTH,
            }
            setGeometry(previous => Object.keys(next).every(key =>
                previous[key as keyof typeof next] === next[key as keyof typeof next],
            ) ? previous : next)
            if (showOutline) {
                const positions = parts.flatMap(part => {
                    const element = partRefs.current.get(part.id)
                    return element ? [{id: part.id, top: element.getBoundingClientRect().top}] : []
                })
                const atBottom = panel.scrollTop > 0
                    && panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 2
                setActivePartId(getActiveOutlinePart(positions, top, atBottom))
            }
        }
        const schedule = () => {
            if (frame === null) frame = requestAnimationFrame(measure)
        }
        const observer = new ResizeObserver(schedule)
        observer.observe(panel)
        observer.observe(root)
        const toolbar = panel.querySelector<HTMLElement>(':scope > .layoutMainToolbar')
        if (toolbar) observer.observe(toolbar)
        // Individual parts can resize without changing the total document height.
        partRefs.current.forEach(element => observer.observe(element))
        panel.addEventListener('scroll', schedule, {passive: true})
        measure()
        return () => {
            observer.disconnect()
            panel.removeEventListener('scroll', schedule)
            if (frame !== null) cancelAnimationFrame(frame)
        }
    }, [documentId, parts, showOutline, zoom])

    useEffect(() => {
        setHighlightedPartId(null)
        return () => {
            if (navigationFrame.current !== null) cancelAnimationFrame(navigationFrame.current)
            if (highlightTimer.current !== null) clearTimeout(highlightTimer.current)
        }
    }, [documentId])

    const closeOutline = () => {
        const panel = rootRef.current?.closest<HTMLElement>('.dvLayout.noChildren')
        panel?.querySelector<HTMLButtonElement>('.documentOutlineToggle')?.focus({preventScroll: true})
        onClose()
    }

    const navigateToPart = (id: string) => {
        if (!partRefs.current.has(id)) return
        if (geometry.narrow) onClose()
        if (navigationFrame.current !== null) cancelAnimationFrame(navigationFrame.current)
        // Wait for the overlay to close before measuring or moving keyboard focus.
        navigationFrame.current = requestAnimationFrame(() => {
            navigationFrame.current = null
            const panel = rootRef.current?.closest<HTMLElement>('.dvLayout.noChildren')
            const target = partRefs.current.get(id)
            if (!panel || !target) return
            const viewport = getViewport(panel)
            panel.scrollTo({
                top: getPartScrollTop(panel.scrollTop, target.getBoundingClientRect().top, viewport.top, viewport.scale),
                behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
            })
            if (geometry.narrow) target.focus({preventScroll: true})
            setActivePartId(id)
            setHighlightedPartId(id)
            if (highlightTimer.current !== null) clearTimeout(highlightTimer.current)
            highlightTimer.current = setTimeout(() => setHighlightedPartId(null), 1800)
        })
    }

    const outlineStyles = {
        '--document-outline-top': `${geometry.top}px`,
        '--document-outline-height': `${geometry.height}px`,
        '--document-outline-width': `${geometry.width}px`,
    } as CSSProperties

    return {rootRef, partRefs, activePartId, highlightedPartId, narrow: geometry.narrow,
        outlineStyles, navigateToPart, closeOutline}
}
